import { useEffect, useMemo, useState } from "react";
import axiosInstance from "../../../api/axiosInstance";
import HeroImageCropDialog from "./HeroImageCropDialog";
import {
  TEXT_RULES,
  ITEM_RULES,
  REPEATER_RULES,
  ADDITIONAL_SCHEMA_RULES,
  validateImageBeforeUpload,
  validateBuyerContent,
} from "./Validation";
import Editor from "../../../components/RichTextEditor/Editor"


const LIVE_BUYER_PAGE_URL = "https://procman.ai/buyer";

const defaults = {
  hero: {
    badge: "AI-Powered Platform for Buyers",
    heading: "Smarter Procurement, Stronger Suppliers, Better Savings",
    description: "",
    primary_cta_label: "Create RFQ Free",
    primary_cta_url: "/create-rfq",
    secondary_cta_label: "Explore Suppliers",
    secondary_cta_url: "/vendors",
  },
  metrics: {
    items: [
      { value: "10,000+", label: "Verified Suppliers", icon: "verified" },
      { value: "500M+", label: "Spend Managed", icon: "procurement" },
      { value: "250+", label: "Enterprise Buyers", icon: "buyers" },
      { value: "98%", label: "On-Time Delivery", icon: "delivery" },
      { value: "35%", label: "Faster Procurement", icon: "faster" },
      { value: "24/7", label: "Support", icon: "support" },
    ],
  },
  pain_points: { title: "Procurement Shouldn't Be This Difficult", items: [] },
  features: {
    title: "Everything Buyers Need. One Smart Platform.",
    cta_label: "Explore All Features",
    items: [],
  },
  working: { heading: "How procman works", items: [] },
  industries: {
    heading: "Procurement solution for every industries",
    items: [],
  },
  banner: {},
  faqs: [],
  faq_cta: {
    faq_title: "Frequently Asked Questions",
    heading: "Ready to Transform Your Procurement?",
    description: "Join hundreds of companies using ProcMan to simplify procurement and drive better business outcomes.",
    primary_cta_label: "Create RFQ Free",
    primary_cta_url: "/create-rfq",
    secondary_cta_label: "Book a Demo",
    secondary_cta_url: "/book-demo",
    benefits: [
      { text: "No Credit Card Required" },
      { text: "Free 30-Day Trial" },
      { text: "Setup in Minutes" },
    ],
  },
  seo: {
    title: "",
    description: "",
    keywords: "",
    canonical_url: LIVE_BUYER_PAGE_URL,
    robots: "index,follow",
    og_title: "",
    og_description: "",
    og_image: "",
    twitter_title: "",
    twitter_description: "",
    twitter_image: "",
    twitter_card: "summary_large_image",
    seo_content: "",
  },
  schema: {
    enable_webpage: true,
    enable_faq: true,
    additional_schema_type: "",
    additional_schema_data: {},
    custom_json: "",
  },
};
const merge = (data = {}) => ({
  ...defaults,
  ...data,
  ...Object.fromEntries(
    [
      "hero",
      "metrics",
      "pain_points",
      "features",
      "working",
      "industries",
      "banner",
      "faq_cta",
      "seo",
      "schema",
    ].map((key) => [key, { ...defaults[key], ...data[key] }]),
  ),
  seo: { ...defaults.seo, ...data.seo, canonical_url: LIVE_BUYER_PAGE_URL },
  faqs: Array.isArray(data.faqs) ? data.faqs : defaults.faqs,
});
const input = {
  width: "100%",
  boxSizing: "border-box",
  padding: "9px 12px",
  border: "1.5px solid #e2e8f0",
  borderRadius: 8,
  marginTop: 5,
  fontSize: 14,
  fontWeight: 400,
};
const HERO_IMAGE_SPECS = {
  desktop: { label: "Desktop / web", width: 1764, height: 774 },
  mobile: { label: "Mobile", width: 768, height: 1024 },
};
const CONTENT_FIELDS = [
  "hero",
  "metrics",
  "pain_points",
  "features",
  "working",
  "industries",
  "banner",
  "faqs",
  "faq_cta",
  "seo",
  "schema",
];
const formatFieldLabel = (key) =>
  key
    .split("_")
    .map((word) => {
      const acronyms = { api: "API", cta: "CTA", json: "JSON", ld: "LD", og: "OG", seo: "SEO", url: "URL" };
      return acronyms[word] || `${word.charAt(0).toUpperCase()}${word.slice(1)}`;
    })
    .join(" ");

const disabledBtn = { opacity: 0.5, cursor: "not-allowed" };

const Card = ({ title, children }) => (
  <section
    style={{
      background: "#fff",
      border: "1px solid #e2e8f0",
      borderRadius: 12,
      padding: "20px 22px",
      marginBottom: 18,
      boxShadow: "0 1px 4px rgba(0,0,0,.05)",
    }}
  >
    <h2 style={{ margin: "0 0 16px", fontSize: 16, color: "#0f172a" }}>
      {title}
    </h2>
    {children}
  </section>
);
const ErrorText = ({ message }) =>
  message ? (
    <div style={{ margin: "0 0 12px", fontSize: 12, color: "#e11d48", fontWeight: 600 }}>
      {message}
    </div>
  ) : null;

const CountHint = ({ count, rules }) =>
  rules?.max ? (
    <span
      style={{
        fontSize: 12,
        fontWeight: 600,
        whiteSpace: "nowrap",
        color: count > rules.max ? "#e11d48" : count === rules.max ? "#b45309" : "#64748b",
      }}
    >
      {count}/{rules.max} {rules.noun}s{count >= rules.max ? " · max reached" : ""}
    </span>
  ) : null;

const ImageError = ({ message }) =>
  message ? (
    <p style={{ margin: "6px 0 0", fontSize: 11, color: "#e11d48", fontWeight: 600, lineHeight: 1.4 }}>
      {message}
    </p>
  ) : null;
const Field = ({ label, value, onChange, textarea, type = "text", readOnly = false, limits, error }) => {
  const len = (value || "").toString().trim().length;
  const showCount = Boolean(limits?.max) && !readOnly;
  const invalid = Boolean(error);
  const fieldStyle = { ...input, ...(invalid ? { borderColor: "#e11d48", background: "#fff1f2" } : {}) };
  return (
    <label
      style={{
        display: "block",
        marginBottom: 14,
        fontSize: 13,
        fontWeight: 600,
        color: "#374151",
      }}
    >
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", gap: 8 }}>
        <span>
          {label}
          {limits?.required && <span style={{ color: "#e11d48" }}> *</span>}
        </span>
        {showCount && (
          <span style={{ fontSize: 11, fontWeight: 600, color: invalid ? "#e11d48" : "#64748b", whiteSpace: "nowrap" }}>
            {len}/{limits.max}
          </span>
        )}
      </div>
      {textarea ? (
        <textarea
          rows={3}
          style={fieldStyle}
          value={value || ""}
          readOnly={readOnly}
          onChange={(e) => onChange(e.target.value)}
        />
      ) : (
        <input
          type={type}
          style={fieldStyle}
          value={value || ""}
          readOnly={readOnly}
          onChange={(e) => onChange(e.target.value)}
        />
      )}
      {limits?.help && !error && (
        <div style={{ marginTop: 5, fontSize: 12, color: "#64748b", fontWeight: 400, lineHeight: 1.4 }}>
          {limits.help}
        </div>
      )}
      {error && <div style={{ marginTop: 5, fontSize: 12, color: "#e11d48", fontWeight: 600 }}>{error}</div>}
    </label>
  );
};
const ADDITIONAL_SCHEMA_FIELDS = {
  Organization: [
    ["name", "Organization name"],
    ["url", "Organization URL"],
    ["description", "Organization description", true],
    ["logo", "Logo URL"],
  ],
  Service: [
    ["name", "Service name"],
    ["description", "Service description", true],
    ["url", "Service URL"],
    ["provider_name", "Provider name"],
  ],
  SoftwareApplication: [
    ["name", "Application name"],
    ["description", "Application description", true],
    ["url", "Application URL"],
    ["application_category", "Application category"],
    ["operating_system", "Operating system"],
  ],
};
const buildAdditionalSchema = (type, values = {}) => {
  const value = (key, fallback = "") => values[key]?.trim() || fallback;
  switch (type) {
    case "Organization":
      return {
        "@context": "https://schema.org",
        "@type": "Organization",
        name: value("name"),
        url: value("url"),
        description: value("description"),
        logo: value("logo"),
      };
    case "Service":
      return {
        "@context": "https://schema.org",
        "@type": "Service",
        name: value("name"),
        description: value("description"),
        url: value("url"),
        provider: {
          "@type": "Organization",
          name: value("provider_name", "Procman"),
        },
      };
    case "SoftwareApplication":
      return {
        "@context": "https://schema.org",
        "@type": "SoftwareApplication",
        name: value("name"),
        description: value("description"),
        url: value("url"),
        applicationCategory: value("application_category", "BusinessApplication"),
        operatingSystem: value("operating_system", "Web"),
      };
    default:
      return null;
  }
};
function AdditionalSchemaGenerator({ schema, onChange, errors }) {
  const [preview, setPreview] = useState(null);
  const selectedType = schema.additional_schema_type || "";
  const values = schema.additional_schema_data || {};
  const fields = ADDITIONAL_SCHEMA_FIELDS[selectedType] || [];
  const updateValue = (key, value) =>
    onChange({
      ...schema,
      additional_schema_data: { ...values, [key]: value },
    });

  return (
    <>
      <label
        style={{ display: "block", fontSize: 13, fontWeight: 600, color: "#374151" }}
      >
        Generate an additional schema
        <select
          style={input}
          value={selectedType}
          onChange={(e) => {
            onChange({
              ...schema,
              additional_schema_type: e.target.value,
              additional_schema_data: {},
            });
            setPreview(null);
          }}
        >
          <option value="">None</option>
          <option value="Organization">Organization</option>
          <option value="Service">Service</option>
          <option value="SoftwareApplication">Software Application</option>
        </select>
      </label>
      {fields.map(([key, label, textarea]) => (
        <Field
          key={key}
          label={label}
          value={values[key]}
          textarea={textarea}
          limits={ADDITIONAL_SCHEMA_RULES[selectedType]?.[key]}
          error={errors?.[`schema.additional.${key}`]}
          onChange={(value) => updateValue(key, value)}
        />
      ))}
      {selectedType && (
        <>
          <button
            type="button"
            className="primary"
            onClick={() => setPreview(buildAdditionalSchema(selectedType, values))}
          >
            Generate schema
          </button>
          {preview && (
            <pre
              style={{
                margin: "14px 0 0",
                padding: 14,
                borderRadius: 8,
                background: "#0f172a",
                color: "#e2e8f0",
                fontSize: 12,
                lineHeight: 1.5,
                overflowX: "auto",
              }}
            >
              {JSON.stringify(preview, null, 2)}
            </pre>
          )}
        </>
      )}
    </>
  );
}
function Repeater({ title, items, fields, onChange, beforeItems, path, errors, itemRules, repeaterRules }) {
  const atMax = Boolean(repeaterRules?.max && items.length >= repeaterRules.max);
  const add = () => {
    if (atMax) return;
    onChange([...items, Object.fromEntries(fields.map(([key]) => [key, ""]))]);
  };
  return (
    <Card title={title}>
      {beforeItems}
      <ErrorText message={errors?.[path]} />
      <button onClick={add} disabled={atMax} style={atMax ? disabledBtn : undefined}>+ Add item</button>
      <CountHint count={items.length} rules={repeaterRules} />
      {items.map((item, index) => (
        <div
          key={index}
          style={{
            borderTop: "1px solid #f1f5f9",
            marginTop: 12,
            paddingTop: 12,
          }}
        >
          <button onClick={() => onChange(items.filter((_, i) => i !== index))}>
            Remove
          </button>
          {fields.map(([key, textarea]) => (
            <Field
              key={key}
              label={formatFieldLabel(key)}
              textarea={textarea}
              value={item[key]}
              limits={itemRules?.[key]}
              error={errors?.[`${path}[${index}].${key}`]}
              onChange={(value) =>
                onChange(
                  items.map((row, i) =>
                    i === index ? { ...row, [key]: value } : row,
                  ),
                )
              }
            />
          ))}
        </div>
      ))}
    </Card>
  );
}
function CtaBenefitsEditor({ record, items, onChange, onUploaded, setNotice, errors }) {
  const repeaterRules = REPEATER_RULES["faq_cta.benefits"];
  const atMax = Boolean(repeaterRules.max && items.length >= repeaterRules.max);
  const normalizedItems = items.map((item) =>
    typeof item === "string" ? { text: item } : item,
  );
  const update = (index, next) =>
    onChange(normalizedItems.map((item, itemIndex) => (itemIndex === index ? { ...item, ...next } : item)));
  return (
    <Card title="CTA benefits">
      <p style={{ margin: "-4px 0 16px", color: "#64748b", fontSize: 13 }}>
        Add a label and optional icon for each CTA benefit.
      </p>
      <ErrorText message={errors?.["faq_cta.benefits"]} />
      <button type="button" className="primary" disabled={atMax} style={atMax ? disabledBtn : undefined} onClick={() => onChange([...normalizedItems, { text: "" }])}>+ Add benefit</button>
      <CountHint count={normalizedItems.length} rules={repeaterRules} />
      {normalizedItems.map((item, index) => (
        <div key={index} style={{ display: "grid", gridTemplateColumns: "minmax(0, 1fr) 180px", gap: 16, marginTop: 14, paddingTop: 14, borderTop: "1px solid #e2e8f0" }}>
          <div style={{ display: "flex", alignItems: "flex-start", gap: 12 }}>
            <div style={{ flex: 1, minWidth: 0 }}>
              <Field
                label="Text"
                value={item.text}
                limits={ITEM_RULES.benefit.text}
                error={errors?.[`faq_cta.benefits[${index}].text`]}
                onChange={(text) => update(index, { text })}
              />
            </div>
            <button
              type="button"
              onClick={() => onChange(normalizedItems.filter((_, itemIndex) => itemIndex !== index))}
              style={{ margin: "26px 0 0", whiteSpace: "nowrap", padding: "9px 14px" }}
            >
              Remove
            </button>
          </div>
          {/* <div style={{ border: "1px dashed #cbd5e1", borderRadius: 8, padding: 10, alignSelf: "start" }}>
            <div style={{ height: 54, marginBottom: 9, display: "grid", placeItems: "center", background: "#f8fafc", borderRadius: 6 }}>
              {item.icon_url ? <img src={item.icon_url} alt="CTA benefit icon" style={{ width: 34, height: 34, objectFit: "contain" }} /> : <span style={{ color: "#94a3b8", fontSize: 11 }}>Default icon</span>}
            </div>
            <input id={`cta-benefit-icon-${index}`} type="file" accept="image/png,image/jpeg,image/webp" disabled={!record || uploading === index} onChange={(event) => uploadIcon(index, event.target.files?.[0])} style={{ display: "none" }} />
            <label htmlFor={`cta-benefit-icon-${index}`} style={{ display: "block", padding: "7px", border: "1px solid #bfdbfe", borderRadius: 6, background: "#eff6ff", color: "#2563eb", textAlign: "center", fontSize: 12, fontWeight: 700, cursor: record && uploading !== index ? "pointer" : "not-allowed", opacity: record && uploading !== index ? 1 : .55 }}>
              {uploading === index ? "Uploading…" : item.icon_url ? "Replace icon" : "Upload icon"}
            </label>
            <ImageError message={imageErrors[index]} />
          </div> */}
        </div>
      ))}
    </Card>
  );
}
function PainPointRepeater({ record, items, onChange, onUploaded, setNotice, errors }) {
  const [uploading, setUploading] = useState(null);
  const [imageErrors, setImageErrors] = useState({});
  const repeaterRules = REPEATER_RULES["pain_points.items"];
  const atMax = Boolean(repeaterRules.max && items.length >= repeaterRules.max);
  const add = () => {
    if (atMax) return;
    onChange([...items, { title: "", description: "" }]);
  };
  const uploadIcon = async (index, file) => {
    if (!file || !record) return;
    const problems = await validateImageBeforeUpload(file, "pain_point_icon");
    setImageErrors((current) => ({ ...current, [index]: problems.join(" ") }));
    if (problems.length) return;
    setUploading(index);
    try {
      const body = new FormData();
      body.append("index", index);
      body.append("image", file);
      const { data } = await axiosInstance.post(
        `/api/superadmin/buyer-page-master/${record.id}/upload-pain-point-icon/`,
        body,
        { headers: { "Content-Type": "multipart/form-data" } },
      );
      const saved = data?.data || data;
      onUploaded(saved);
      onChange(saved?.pain_points?.items || items);
      setNotice("Pain-point icon uploaded successfully.");
    } catch (error) {
      setNotice(
        error?.response?.data?.detail ||
        "Could not upload the pain-point icon.",
      );
    } finally {
      setUploading(null);
    }
  };
  return (
    <Card title="Procurement pain points">
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 16,
          margin: "-4px 0 18px",
          paddingBottom: 16,
          borderBottom: "1px solid #e2e8f0",
        }}
      >
        <p
          style={{ margin: 0, fontSize: 13, color: "#64748b", lineHeight: 1.5 }}
        >
          Add concise buying challenges and an optional icon. Save newly added
          items before uploading an icon.
        </p>
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <CountHint count={items.length} rules={repeaterRules} />
          <button type="button" className="primary" onClick={add} disabled={atMax}
            title={atMax ? `Maximum ${repeaterRules.max} reached` : undefined}
            style={{ whiteSpace: "nowrap", padding: "8px 12px", ...(atMax ? disabledBtn : {}) }}>
            + Add pain point
          </button>
        </div>
      </div>
      <ErrorText message={errors?.["pain_points.items"]} />
      <div style={{ display: "grid", gap: 14 }}>
        {items.map((item, index) => (
          <article
            key={index}
            style={{
              border: "1px solid #dbe4f0",
              borderRadius: 10,
              background: "#f8fafc",
              overflow: "hidden",
            }}
          >
            <header
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                gap: 12,
                padding: "11px 14px",
                background: "#fff",
                borderBottom: "1px solid #e2e8f0",
              }}
            >
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 9,
                  color: "#0f172a",
                  fontSize: 13,
                  fontWeight: 700,
                }}
              >
                <span
                  style={{
                    display: "inline-grid",
                    placeItems: "center",
                    width: 23,
                    height: 23,
                    borderRadius: "50%",
                    background: "#e0e7ff",
                    color: "#4f46e5",
                    fontSize: 12,
                  }}
                >
                  {index + 1}
                </span>
                {item.title || "Untitled pain point"}
              </div>
              <button
                type="button"
                onClick={() => onChange(items.filter((_, i) => i !== index))}
                style={{
                  margin: 0,
                  borderColor: "#fecaca",
                  background: "#fff1f2",
                  color: "#e11d48",
                }}
              >
                Remove
              </button>
            </header>
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "minmax(0, 1fr) 190px",
                gap: 18,
                padding: 14,
              }}
            >
              <div>
                <Field
                  label="Title"
                  value={item.title}
                  limits={ITEM_RULES.pain_point.title}
                  error={errors?.[`pain_points.items[${index}].title`]}
                  onChange={(value) =>
                    onChange(
                      items.map((row, i) =>
                        i === index ? { ...row, title: value } : row,
                      ),
                    )
                  }
                />
                <Field
                  label="Description"
                  textarea
                  value={item.description}
                  limits={ITEM_RULES.pain_point.description}
                  error={errors?.[`pain_points.items[${index}].description`]}
                  onChange={(value) =>
                    onChange(
                      items.map((row, i) =>
                        i === index ? { ...row, description: value } : row,
                      ),
                    )
                  }
                />
              </div>
              <div
                style={{
                  border: "1px dashed #cbd5e1",
                  borderRadius: 8,
                  background: "#fff",
                  padding: 12,
                  alignSelf: "start",
                }}
              >
                <div
                  style={{
                    marginBottom: 9,
                    fontSize: 12,
                    fontWeight: 700,
                    color: "#475569",
                  }}
                >
                  ICON
                </div>
                <div
                  style={{
                    height: 72,
                    marginBottom: 10,
                    borderRadius: 7,
                    background: "#f8fafc",
                    display: "grid",
                    placeItems: "center",
                  }}
                >
                  {item.icon_url ? (
                    <img
                      src={item.icon_url}
                      alt="Current pain-point icon"
                      style={{ width: 54, height: 54, objectFit: "contain" }}
                    />
                  ) : (
                    <span style={{ color: "#94a3b8", fontSize: 12 }}>
                      No icon selected
                    </span>
                  )}
                </div>
                <input
                  id={`pain-point-icon-${index}`}
                  type="file"
                  accept="image/png,image/jpeg,image/webp"
                  disabled={!record || uploading === index}
                  onChange={(event) =>
                    uploadIcon(index, event.target.files?.[0])
                  }
                  style={{ display: "none" }}
                />
                <label
                  htmlFor={`pain-point-icon-${index}`}
                  style={{
                    display: "block",
                    border: "1px solid #bfdbfe",
                    borderRadius: 6,
                    padding: "7px 8px",
                    background: "#eff6ff",
                    color: "#2563eb",
                    textAlign: "center",
                    fontSize: 12,
                    fontWeight: 700,
                    cursor:
                      record && uploading !== index ? "pointer" : "not-allowed",
                    opacity: record && uploading !== index ? 1 : 0.55,
                  }}
                >
                  {uploading === index
                    ? "Uploading…"
                    : item.icon_url
                      ? "Replace icon"
                      : "Upload icon"}
                </label>
                <p
                  style={{
                    margin: "8px 0 0",
                    color: "#94a3b8",
                    fontSize: 10.5,
                    textAlign: "center",
                  }}
                >
                  PNG, JPEG, or WebP
                </p>
                <ImageError message={imageErrors[index]} />
              </div>
            </div>
          </article>
        ))}
      </div>
      {!items.length && (
        <div
          style={{
            padding: "26px 16px",
            border: "1px dashed #cbd5e1",
            borderRadius: 10,
            color: "#64748b",
            textAlign: "center",
            fontSize: 13,
          }}
        >
          No pain points yet. Add your first one to get started.
        </div>
      )}
    </Card>
  );
}
function ContentCardRepeater({
  title,
  items,
  onChange,
  titleKey,
  descriptionKey,
  record,
  onUploaded,
  setNotice,
  iconUploadPath,
  saveBeforeUpload,
  path,
  errors,
  itemRules,
  repeaterRules,
}) {
  const [uploading, setUploading] = useState(null);
  const [imageErrors, setImageErrors] = useState({});
  const atMax = Boolean(repeaterRules?.max && items.length >= repeaterRules.max);
  const update = (index, key, value) =>
    onChange(
      items.map((item, itemIndex) =>
        itemIndex === index ? { ...item, [key]: value } : item,
      ),
    );
  const add = () => {
    if (atMax) return;
    onChange([...items, { [titleKey]: "", [descriptionKey]: "" }]);
  };
  const uploadIcon = async (index, file) => {
    if (!file) return;
    const problems = await validateImageBeforeUpload(file, "working_icon");
    setImageErrors((current) => ({ ...current, [index]: problems.join(" ") }));
    if (problems.length) return;
    setUploading(index);
    try {
      let pageRecord = record;
      if (saveBeforeUpload) {
        pageRecord = await saveBeforeUpload();
        if (!pageRecord) return;
      }
      if (!pageRecord) {
        setNotice("Save the Buyer page before uploading an icon.");
        return;
      }
      const body = new FormData();
      body.append("index", index);
      body.append("image", file);
      const { data } = await axiosInstance.post(
        `/api/superadmin/buyer-page-master/${pageRecord.id}/${iconUploadPath}/`,
        body,
        { headers: { "Content-Type": "multipart/form-data" } },
      );
      const saved = data?.data || data;
      onUploaded(saved);
      onChange(saved?.working?.items || items);
      setNotice("Workflow icon uploaded successfully.");
    } catch (error) {
      setNotice(
        error?.response?.data?.detail || "Could not upload the workflow icon.",
      );
    } finally {
      setUploading(null);
    }
  };
  const isWorking = Boolean(iconUploadPath);
  return (
    <Card title={title}>
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 16,
          margin: "-4px 0 18px",
          paddingBottom: 16,
          borderBottom: "1px solid #e2e8f0",
        }}
      >
        <p
          style={{ margin: 0, color: "#64748b", fontSize: 13, lineHeight: 1.5 }}
        >
          {isWorking
            ? "Describe each step in the buyer journey. Save new steps before uploading an icon."
            : "Organize the key capabilities buyers can expect from the platform."}
        </p>
        <button
          type="button"
          className="primary"
          onClick={add}
          disabled={atMax}
          style={{ whiteSpace: "nowrap", padding: "8px 12px", ...(atMax ? disabledBtn : {}) }}
        >
          + Add {isWorking ? "step" : "feature"}
        </button>
      </div>
      <ErrorText message={errors?.[path]} />
      <div style={{ display: "grid", gap: 14 }}>
        {items.map((item, index) => (
          <article
            key={index}
            style={{
              border: "1px solid #dbe4f0",
              borderRadius: 10,
              background: "#f8fafc",
              overflow: "hidden",
            }}
          >
            <header
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                gap: 12,
                padding: "11px 14px",
                background: "#fff",
                borderBottom: "1px solid #e2e8f0",
              }}
            >
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 9,
                  color: "#0f172a",
                  fontWeight: 700,
                  fontSize: 13,
                }}
              >
                <span
                  style={{
                    display: "inline-grid",
                    placeItems: "center",
                    width: 23,
                    height: 23,
                    borderRadius: "50%",
                    background: "#e0e7ff",
                    color: "#4f46e5",
                    fontSize: 12,
                  }}
                >
                  {index + 1}
                </span>
                {item[titleKey] || `Untitled ${isWorking ? "step" : "feature"}`}
              </div>
              <button
                type="button"
                onClick={() =>
                  onChange(items.filter((_, itemIndex) => itemIndex !== index))
                }
                style={{
                  margin: 0,
                  borderColor: "#fecaca",
                  background: "#fff1f2",
                  color: "#e11d48",
                }}
              >
                Remove
              </button>
            </header>
            <div
              style={{
                display: "grid",
                gridTemplateColumns: isWorking ? "minmax(0, 1fr) 190px" : "1fr",
                gap: 18,
                padding: 14,
              }}
            >
              <div>
                <Field
                  label={isWorking ? "Step label" : "Feature title"}
                  value={item[titleKey]}
                  limits={itemRules?.[titleKey]}
                  error={errors?.[`${path}[${index}].${titleKey}`]}
                  onChange={(value) => update(index, titleKey, value)}
                />
                <Field
                  label={isWorking ? "Step description" : "Feature description"}
                  textarea
                  value={item[descriptionKey]}
                  limits={itemRules?.[descriptionKey]}
                  error={errors?.[`${path}[${index}].${descriptionKey}`]}
                  onChange={(value) => update(index, descriptionKey, value)}
                />
              </div>
              {isWorking && (
                <div
                  style={{
                    border: "1px dashed #cbd5e1",
                    borderRadius: 8,
                    background: "#fff",
                    padding: 12,
                    alignSelf: "start",
                  }}
                >
                  <div
                    style={{
                      marginBottom: 9,
                      fontSize: 12,
                      fontWeight: 700,
                      color: "#475569",
                    }}
                  >
                    STEP ICON
                  </div>
                  <div
                    style={{
                      height: 72,
                      marginBottom: 10,
                      borderRadius: 7,
                      background: "#f8fafc",
                      display: "grid",
                      placeItems: "center",
                    }}
                  >
                    {item.icon_url ? (
                      <img
                        src={item.icon_url}
                        alt="Current workflow icon"
                        style={{ width: 54, height: 54, objectFit: "contain" }}
                      />
                    ) : (
                      <span style={{ color: "#94a3b8", fontSize: 12 }}>
                        No icon selected
                      </span>
                    )}
                  </div>
                  <input
                    id={`working-icon-${index}`}
                    type="file"
                    accept="image/png,image/jpeg,image/webp"
                    disabled={(!record && !saveBeforeUpload) || uploading === index}
                    onChange={(event) => {
                      const file = event.target.files?.[0];
                      event.target.value = "";
                      uploadIcon(index, file);
                    }}
                    style={{ display: "none" }}
                  />
                  <button
                    type="button"
                    disabled={(!record && !saveBeforeUpload) || uploading === index}
                    onClick={() =>
                      document.getElementById(`working-icon-${index}`)?.click()
                    }
                    style={{
                      display: "block",
                      width: "100%",
                      boxSizing: "border-box",
                      margin: 0,
                      border: "1px solid #bfdbfe",
                      borderRadius: 6,
                      padding: "7px 8px",
                      background: "#eff6ff",
                      color: "#2563eb",
                      textAlign: "center",
                      fontSize: 12,
                      fontWeight: 700,
                      cursor:
                        (record || saveBeforeUpload) && uploading !== index
                          ? "pointer"
                          : "not-allowed",
                      opacity: (record || saveBeforeUpload) && uploading !== index ? 1 : 0.55,
                    }}
                  >
                    {uploading === index
                      ? "Uploading…"
                      : item.icon_url
                        ? "Replace icon"
                        : "Upload icon"}
                  </button>
                  <p
                    style={{
                      margin: "8px 0 0",
                      color: "#94a3b8",
                      fontSize: 10.5,
                      textAlign: "center",
                    }}
                  >
                    PNG, JPEG, or WebP
                  </p>
                  <ImageError message={imageErrors[index]} />
                </div>
              )}
            </div>
          </article>
        ))}
      </div>
      {!items.length && (
        <div
          style={{
            padding: "26px 16px",
            border: "1px dashed #cbd5e1",
            borderRadius: 10,
            color: "#64748b",
            textAlign: "center",
            fontSize: 13,
          }}
        >
          No {isWorking ? "workflow steps" : "features"} yet. Add your first one
          to get started.
        </div>
      )}
    </Card>
  );
}
function SeoImageField({ label, record, seoKey, value, hasCustom, defaultNote, onUploaded, setNotice }) {
  const [uploading, setUploading] = useState(false);
  const [imageError, setImageError] = useState("");
  const upload = async (file) => {
    if (!file || !record) return;
    const problems = await validateImageBeforeUpload(file, seoKey);
    setImageError(problems.join(" "));
    if (problems.length) return;
    setUploading(true);
    try {
      const body = new FormData();
      body.append("image", file);
      const { data } = await axiosInstance.post(
        `/api/superadmin/buyer-page-master/${record.id}/upload-${seoKey.replace("_", "-")}/`,
        body,
        { headers: { "Content-Type": "multipart/form-data" } },
      );
      onUploaded(data?.data || data);
      setNotice(`${label} uploaded successfully.`);
    } catch (error) {
      setNotice(error?.response?.data?.detail || `Could not upload ${label}.`);
    } finally {
      setUploading(false);
    }
  };
  return (
    <div style={{ marginBottom: 14 }}>
      <div style={{ fontSize: 13, fontWeight: 600, color: "#374151", marginBottom: 7 }}>{label}</div>
      <div style={{ display: "grid", gridTemplateColumns: "140px 1fr", gap: 12, alignItems: "center" }}>
        <div style={{ height: 78, width: 140, borderRadius: 8, border: "1px dashed #cbd5e1", background: "#f8fafc", overflow: "hidden", display: "grid", placeItems: "center" }}>
          {value ? (
            <img src={value} alt={label} style={{ width: "100%", height: "100%", objectFit: "cover" }} />
          ) : (
            <span style={{ color: "#94a3b8", fontSize: 11 }}>No image</span>
          )}
        </div>
        <div>
          <input
            id={`seo-${seoKey}`}
            type="file"
            accept="image/png,image/jpeg,image/webp"
            disabled={!record || uploading}
            onChange={(e) => { const file = e.target.files?.[0]; e.target.value = ""; upload(file); }}
            style={{ display: "none" }}
          />
          <label
            htmlFor={`seo-${seoKey}`}
            style={{ display: "inline-block", border: "1px solid #bfdbfe", borderRadius: 6, padding: "7px 12px", background: "#eff6ff", color: "#2563eb", fontSize: 12, fontWeight: 700, cursor: record && !uploading ? "pointer" : "not-allowed", opacity: record && !uploading ? 1 : 0.55 }}
          >
            {uploading ? "Uploading…" : hasCustom ? "Replace image" : "Upload image"}
          </label>
          {!hasCustom && value && (
            <p style={{ margin: "8px 0 0", color: "#94a3b8", fontSize: 11 }}>{defaultNote}</p>
          )}
          <ImageError message={imageError} />
        </div>
      </div>
    </div>
  );
}

function MetricsEditor({ record, value, onChange, onUploaded, setNotice, errors }) {
  const [uploading, setUploading] = useState(null);
  const [imageErrors, setImageErrors] = useState({});
  const items = value.items || [];
  const repeaterRules = REPEATER_RULES["metrics.items"];
  const atMax = Boolean(repeaterRules.max && items.length >= repeaterRules.max);
  const add = () => {
    if (atMax) return;
    onChange({ ...value, items: [...items, { value: "", label: "" }] });
  };
  const remove = (index) =>
    onChange({
      ...value,
      items: items.filter((_, itemIndex) => itemIndex !== index),
    });
  const update = (index, key, next) =>
    onChange({
      ...value,
      items: items.map((item, itemIndex) =>
        itemIndex === index ? { ...item, [key]: next } : item,
      ),
    });
  const uploadIcon = async (index, file) => {
    if (!file || !record) return;
    const problems = await validateImageBeforeUpload(file, "metric_icon");
    setImageErrors((current) => ({ ...current, [index]: problems.join(" ") }));
    if (problems.length) return;
    setUploading(index);
    try {
      const body = new FormData();
      body.append("index", index);
      body.append("items", JSON.stringify(items));
      body.append("image", file);
      const { data } = await axiosInstance.post(
        `/api/superadmin/buyer-page-master/${record.id}/upload-metric-icon/`,
        body,
        { headers: { "Content-Type": "multipart/form-data" } },
      );
      const saved = data?.data || data;
      onUploaded(saved);
      onChange(saved?.metrics || value);
      setNotice("Metric icon uploaded successfully.");
    } catch (error) {
      setNotice(
        error?.response?.data?.detail || "Could not upload the metric icon.",
      );
    } finally {
      setUploading(null);
    }
  };
  return (
    <Card title="Buyer page metrics">
      <p style={{ margin: "-4px 0 18px", color: "#64748b", fontSize: 13 }}>
        Edit the performance metrics strip shown between the logo marquee and
        Procurement Pain Points. Save new metrics before uploading their icons.
      </p>
      <ErrorText message={errors?.["metrics.items"]} />
      <button type="button" className="primary" onClick={add} disabled={atMax} style={{ marginBottom: 14, ...(atMax ? disabledBtn : {}) }}>
        + Add metric
      </button>
      <CountHint count={items.length} rules={repeaterRules} />
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(300px, 1fr))",
          gap: 12,
        }}
      >
        {items.map((item, index) => (
          <div
            key={index}
            style={{
              padding: 14,
              border: "1px solid #dbe4f0",
              borderRadius: 10,
              background: "#f8fafc",
              display: "grid",
              gridTemplateColumns: "1fr 82px",
              gap: 12,
            }}
          >
            <div>
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 8,
                  marginBottom: 10,
                  fontSize: 12,
                  fontWeight: 700,
                  color: "#475569",
                }}
              >
                <span
                  style={{
                    display: "inline-grid",
                    placeItems: "center",
                    width: 22,
                    height: 22,
                    borderRadius: "50%",
                    background: "#e0e7ff",
                    color: "#4f46e5",
                  }}
                >
                  {index + 1}
                </span>{" "}
                Metric
                <button
                  type="button"
                  onClick={() => remove(index)}
                  style={{ margin: "0 0 0 auto", borderColor: "#fecaca", background: "#fff1f2", color: "#e11d48" }}
                >
                  Remove
                </button>
              </div>
              <Field
                label="Value"
                value={item.value}
                limits={ITEM_RULES.metric.value}
                error={errors?.[`metrics.items[${index}].value`]}
                onChange={(next) => update(index, "value", next)}
              />
              <Field
                label="Label"
                value={item.label}
                limits={ITEM_RULES.metric.label}
                error={errors?.[`metrics.items[${index}].label`]}
                onChange={(next) => update(index, "label", next)}
              />
            </div>
            <div style={{ paddingTop: 30 }}>
              <div
                style={{
                  height: 56,
                  marginBottom: 8,
                  borderRadius: 8,
                  background: "#fff",
                  border: "1px dashed #cbd5e1",
                  display: "grid",
                  placeItems: "center",
                }}
              >
                {item.icon_url ? (
                  <img
                    src={item.icon_url}
                    alt="Metric icon"
                    style={{ width: 36, height: 36, objectFit: "contain" }}
                  />
                ) : (
                  <span
                    style={{
                      color: "#94a3b8",
                      fontSize: 10,
                      textAlign: "center",
                    }}
                  >
                    Default icon
                  </span>
                )}
              </div>
              <input
                id={`metric-icon-${index}`}
                type="file"
                accept="image/png,image/jpeg,image/webp"
                disabled={!record || uploading === index}
                onChange={(event) => uploadIcon(index, event.target.files?.[0])}
                style={{ display: "none" }}
              />
              <label
                htmlFor={`metric-icon-${index}`}
                style={{
                  display: "block",
                  border: "1px solid #bfdbfe",
                  borderRadius: 6,
                  padding: "6px 4px",
                  background: "#eff6ff",
                  color: "#2563eb",
                  textAlign: "center",
                  fontSize: 11,
                  fontWeight: 700,
                  cursor:
                    record && uploading !== index ? "pointer" : "not-allowed",
                  opacity: record && uploading !== index ? 1 : 0.55,
                }}
              >
                {uploading === index
                  ? "Uploading…"
                  : item.icon_url
                    ? "Replace"
                    : "Upload"}
              </label>
              <ImageError message={imageErrors[index]} />
            </div>
          </div>
        ))}
      </div>
    </Card>
  );
}
function HeroImageUploads({ record, onUploaded, setNotice }) {
  const [uploading, setUploading] = useState("");
  const [previews, setPreviews] = useState({});
  const [overlayUrl, setOverlayUrl] = useState("");
  const [cropRequest, setCropRequest] = useState(null);
  const [imageErrors, setImageErrors] = useState({});
  const selectImage = async (variant, file) => {
    if (!file || !record) return;
    const problems = await validateImageBeforeUpload(file, `hero_${variant}`);
    setImageErrors((current) => ({ ...current, [variant]: problems.join(" ") }));
    if (problems.length) return;
    const source = URL.createObjectURL(file);
    const image = new Image();
    image.onload = async () => {
      URL.revokeObjectURL(source);
      setCropRequest({ variant, file });
    };
    image.onerror = () => {
      URL.revokeObjectURL(source);
      setNotice("Select a valid image file.");
    };
    image.src = source;
  };
  const uploadCroppedImage = async (file) => {
    const variant = cropRequest.variant;
    const spec = HERO_IMAGE_SPECS[variant];
    const preview = URL.createObjectURL(file);
    setCropRequest(null);
    setPreviews((current) => ({ ...current, [variant]: preview }));
    setUploading(variant);
    try {
      const body = new FormData();
      body.append("variant", variant);
      body.append("image", file);
      const { data } = await axiosInstance.post(
        `/api/superadmin/buyer-page-master/${record.id}/upload-hero-image/`,
        body,
        { headers: { "Content-Type": "multipart/form-data" } },
      );
      onUploaded(data?.data || data);
      setNotice(`${spec.label} hero image uploaded successfully.`);
    } catch (error) {
      setNotice(
        error?.response?.data?.detail || "Could not upload hero image.",
      );
    } finally {
      setUploading("");
    }
  };
  return (
    <>
      {cropRequest && (
        <HeroImageCropDialog
          file={cropRequest.file}
          variant={cropRequest.variant}
          spec={HERO_IMAGE_SPECS[cropRequest.variant]}
          onCancel={() => setCropRequest(null)}
          onCrop={uploadCroppedImage}
        />
      )}
      <Card title="Hero background images">
        <p style={{ margin: "0 0 16px", fontSize: 13, color: "#64748b" }}>
          Select an image, then crop it to the required size. Click a preview to
          view the full image.
        </p>
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))",
            gap: 16,
          }}
        >
          {Object.entries(HERO_IMAGE_SPECS).map(([variant, spec]) => {
            const imageUrl =
              previews[variant] || record?.[`hero_${variant}_image`];
            return (
              <div key={variant} style={{ minWidth: 0 }}>
                <div
                  style={{
                    fontSize: 13,
                    fontWeight: 600,
                    color: "#374151",
                    marginBottom: 7,
                  }}
                >
                  {spec.label}: {spec.width} × {spec.height}px
                </div>
                {imageUrl && (
                  <button
                    type="button"
                    aria-label={`View ${spec.label} hero image`}
                    onClick={() => setOverlayUrl(imageUrl)}
                    style={{
                      padding: 0,
                      margin: "0 0 9px",
                      border: 0,
                      background: "none",
                      cursor: "zoom-in",
                    }}
                  >
                    <img
                      src={imageUrl}
                      alt={`${spec.label} hero preview`}
                      style={{
                        display: "block",
                        width: 220,
                        height: 96,
                        objectFit: "cover",
                        borderRadius: 8,
                        border: "1px solid #e2e8f0",
                      }}
                    />
                  </button>
                )}
                <input
                  type="file"
                  accept="image/png,image/jpeg,image/webp"
                  disabled={!record || uploading === variant}
                  onChange={(event) =>
                    selectImage(variant, event.target.files?.[0])
                  }
                  style={input}
                />
                <ImageError message={imageErrors[variant]} />
                {uploading === variant && (
                  <p
                    style={{
                      margin: "6px 0 0",
                      fontSize: 12,
                      color: "#2563eb",
                    }}
                  >
                    Uploading...
                  </p>
                )}
              </div>
            );
          })}
        </div>
      </Card>
      {overlayUrl && (
        <div
          role="dialog"
          aria-modal="true"
          aria-label="Hero image preview"
          onClick={() => setOverlayUrl("")}
          style={{
            position: "fixed",
            inset: 0,
            zIndex: 1000,
            padding: 24,
            background: "rgba(15,23,42,.82)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <img
            src={overlayUrl}
            alt="Full hero preview"
            onClick={(event) => event.stopPropagation()}
            style={{
              maxWidth: "100%",
              maxHeight: "100%",
              objectFit: "contain",
              borderRadius: 10,
              background: "#fff",
            }}
          />
          <button
            type="button"
            onClick={() => setOverlayUrl("")}
            style={{
              position: "fixed",
              top: 18,
              right: 18,
              margin: 0,
              border: "1px solid #fff",
              background: "rgba(15,23,42,.65)",
              color: "#fff",
              fontSize: 18,
              padding: "8px 12px",
            }}
          >
            ×
          </button>
        </div>
      )}
    </>
  );
}

export default function BuyerPageMaster() {
  const [record, setRecord] = useState(null);
  const [content, setContent] = useState(defaults);
  const [notice, setNotice] = useState("");
  const [saving, setSaving] = useState(false);
  const errors = useMemo(() => validateBuyerContent(content), [content]);
  const errorCount = Object.keys(errors).length;
  const hasBlockingErrors = errorCount > 0;
  // limits + error props for a scalar field, e.g. fp("hero.heading")
  const fp = (ruleKey) => ({ limits: TEXT_RULES[ruleKey], error: errors[ruleKey] });
  useEffect(() => {
    axiosInstance
      .get("/api/superadmin/buyer-page-master/")
      .then(({ data }) => {
        const row = data?.results?.[0] || data?.[0];
        if (row) {
          setRecord(row);
          setContent(merge(row));
        }
      })
      .catch(() => setNotice("Could not load Buyer Page Master."));
  }, []);
  const setGroup = (group, key, value) =>
    setContent((state) => {
      const nextGroup = { ...state[group], [key]: value };

      // Keep social metadata aligned with the primary metadata entered here.
      // This ensures previews use the same title and description by default.
      if (group === "seo" && key === "title") {
        nextGroup.og_title = value;
        nextGroup.twitter_title = value;
      }
      if (group === "seo" && key === "description") {
        nextGroup.og_description = value;
        nextGroup.twitter_description = value;
      }

      return { ...state, [group]: nextGroup };
    });
  const save = async () => {
    if (hasBlockingErrors) {
      setNotice(`Fix ${errorCount} validation error(s) before saving.`);
      return null;
    }
    const payload = Object.fromEntries(
      CONTENT_FIELDS.map((field) => [field, content[field]]),
    );
    setSaving(true);
    try {
      const response = record
        ? await axiosInstance.put(
          `/api/superadmin/buyer-page-master/${record.id}/`,
          payload,
        )
        : await axiosInstance.post(
          "/api/superadmin/buyer-page-master/",
          payload,
        );
      const saved = response.data?.data || response.data;
      setRecord(saved);
      setContent(merge(saved));
      setNotice("Buyer page saved successfully.");
      return saved;
    } catch (error) {
      const errors = error?.response?.data;
      setNotice(
        errors?.detail ||
        (errors
          ? Object.values(errors).flat().join(" ")
          : "Could not save Buyer page."),
      );
      return null;
    } finally {
      setSaving(false);
    }
  };
  return (
    <div
      className="buyer-cms"
      style={{
        minHeight: "100vh",
        padding: 24,
        background: "#f8fafc",
        fontFamily: "Inter,Arial,sans-serif",
      }}
    >
      <style>{`.buyer-cms button:not(:where(.rte-wrapper *)){border:1px solid #bfdbfe;border-radius:7px;background:#eff6ff;color:#2563eb;padding:6px 10px;font-size:12px;font-weight:600;cursor:pointer;margin-right:6px}.buyer-cms .primary{border:0;background:#2563eb;color:#fff;padding:10px 16px;font-size:13px}.buyer-cms input:focus,.buyer-cms textarea:focus{outline:none;border-color:#3b82f6;box-shadow:0 0 0 3px rgba(59,130,246,.12)}`}</style>
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          gap: 16,
          marginBottom: 20,
        }}
      >
        <div>
          <h1 style={{ margin: 0, fontSize: 22, color: "#0f172a" }}>
            Buyer Page
          </h1>
          <p style={{ margin: "5px 0", fontSize: 13, color: "#64748b" }}>
            Manage the live Buyer page content, SEO, and structured data.
          </p>
        </div>
        <div
          style={{
            position: "fixed",
            top: 88,
            right: 48,
            zIndex: 1200,
            display: "flex",
            flexDirection: "column",
            alignItems: "flex-end",
            gap: 6,
          }}
        >
          <button
            className="primary"
            disabled={saving || hasBlockingErrors}
            onClick={save}
            title={hasBlockingErrors ? `${errorCount} field(s) need fixing before you can save` : undefined}
            style={{
              margin: 0,
              boxShadow: "0 6px 16px rgba(37,99,235,.3)",
              opacity: hasBlockingErrors ? 0.5 : 1,
              cursor: hasBlockingErrors ? "not-allowed" : "pointer",
            }}
          >
            {saving ? "Saving..." : "Save Data"}
          </button>
          {hasBlockingErrors && (
            <span style={{ fontSize: 11, fontWeight: 700, color: "#e11d48", background: "#fff1f2", border: "1px solid #fecaca", borderRadius: 6, padding: "3px 8px" }}>
              {errorCount} field(s) need fixing
            </span>
          )}
        </div>
      </div>
      {notice && (
        <div
          style={{
            padding: "10px 14px",
            marginBottom: 16,
            borderRadius: 8,
            background: notice.includes("success") ? "#f0fdf4" : "#fef2f2",
            color: notice.includes("success") ? "#166534" : "#b91c1c",
          }}
        >
          {notice}
        </div>
      )}
      <Card title="Hero">
        {Object.entries(content.hero).map(([key, value]) => (
          <Field
            key={key}
            label={formatFieldLabel(key)}
            value={value}
            textarea={key === "description"}
            {...fp(`hero.${key}`)}
            onChange={(value) => setGroup("hero", key, value)}
          />
        ))}
      </Card>
      <HeroImageUploads
        record={record}
        onUploaded={setRecord}
        setNotice={setNotice}
      />
      <MetricsEditor
        record={record}
        value={content.metrics}
        onChange={(metrics) => setContent((state) => ({ ...state, metrics }))}
        onUploaded={setRecord}
        setNotice={setNotice}
        errors={errors}
      />
      <PainPointRepeater
        record={record}
        items={content.pain_points.items || []}
        onChange={(items) => setGroup("pain_points", "items", items)}
        onUploaded={setRecord}
        setNotice={setNotice}
        errors={errors}
      />
      <ContentCardRepeater
        title="Platform features"
        items={content.features.items || []}
        titleKey="title"
        descriptionKey="description"
        path="features.items"
        errors={errors}
        itemRules={ITEM_RULES.feature}
        repeaterRules={REPEATER_RULES["features.items"]}
        onChange={(items) => setGroup("features", "items", items)}
      />
      <ContentCardRepeater
        title="How it works"
        items={content.working.items || []}
        titleKey="label"
        descriptionKey="desc"
        record={record}
        onUploaded={setRecord}
        setNotice={setNotice}
        iconUploadPath="upload-working-icon"
        saveBeforeUpload={save}
        path="working.items"
        errors={errors}
        itemRules={ITEM_RULES.working}
        repeaterRules={REPEATER_RULES["working.items"]}
        onChange={(items) => setGroup("working", "items", items)}
      />
      {/* <BannerEditor
        record={record}
        value={content.banner}
        onChange={(banner) => setContent((state) => ({ ...state, banner }))}
        onUploaded={setRecord}
        setNotice={setNotice}
        errors={errors}
      /> */}
      <Repeater
        title="FAQs"
        items={content.faqs || []}
        fields={[["question"], ["answer", true]]}
        path="faqs"
        errors={errors}
        itemRules={ITEM_RULES.faq}
        repeaterRules={REPEATER_RULES.faqs}
        onChange={(faqs) => setContent((state) => ({ ...state, faqs }))}
        beforeItems={
          <Field
            label="FAQ Heading"
            value={content.faq_cta.faq_title}
            {...fp("faq_cta.faq_title")}
            onChange={(value) => setGroup("faq_cta", "faq_title", value)}
          />
        }
      />
      <Card title="CTA section">
        <p style={{ margin: "-4px 0 18px", color: "#64748b", fontSize: 13, lineHeight: 1.5 }}>
          Edit the purple call-to-action card displayed beside the FAQs.
        </p>
        <Field label="CTA Heading" value={content.faq_cta.heading} {...fp("faq_cta.heading")} onChange={(value) => setGroup("faq_cta", "heading", value)} />
        <Field label="CTA Description" textarea value={content.faq_cta.description} {...fp("faq_cta.description")} onChange={(value) => setGroup("faq_cta", "description", value)} />
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14 }}>
          <Field label="Primary Button Label" value={content.faq_cta.primary_cta_label} {...fp("faq_cta.primary_cta_label")} onChange={(value) => setGroup("faq_cta", "primary_cta_label", value)} />
          <Field label="Primary Button URL" value={content.faq_cta.primary_cta_url} {...fp("faq_cta.primary_cta_url")} onChange={(value) => setGroup("faq_cta", "primary_cta_url", value)} />
          <Field label="Secondary Button Label" value={content.faq_cta.secondary_cta_label} {...fp("faq_cta.secondary_cta_label")} onChange={(value) => setGroup("faq_cta", "secondary_cta_label", value)} />
          <Field label="Secondary Button URL" value={content.faq_cta.secondary_cta_url} {...fp("faq_cta.secondary_cta_url")} onChange={(value) => setGroup("faq_cta", "secondary_cta_url", value)} />
        </div>
        <CtaBenefitsEditor
          record={record}
          items={content.faq_cta.benefits || []}
          onChange={(benefits) => setGroup("faq_cta", "benefits", benefits)}
          onUploaded={setRecord}
          setNotice={setNotice}
          errors={errors}
        />
      </Card>
      <Card title="SEO">
        {Object.entries(content.seo).map(([key, value]) => {
          if (key === "og_image" || key === "twitter_image") return null;
          if (key === "seo_content") {
            return (
              <div key={key} style={{ marginBottom: 14 }}>
                <div style={{ fontSize: 13, fontWeight: 600, color: "#374151", marginBottom: 7 }}>SEO Content</div>
                <div className="rte-wrapper">
                  <Editor
                    value={value || ""}
                    onChange={(html) => setGroup("seo", "seo_content", html)}
                    placeholder="Write SEO content..."
                    minHeight={300}
                  />
                </div>
              </div>
            );
          }
          const isCanonicalUrl = key === "canonical_url";
          return (
            <Field
              key={key}
              label={formatFieldLabel(key)}
              value={value}
              textarea={key.includes("description") || key === "seo_content"}
              readOnly={isCanonicalUrl}
              {...fp(`seo.${key}`)}
              onChange={(value) => setGroup("seo", key, value)}
            />
          );
        })}
        <SeoImageField
          label="OG Image"
          record={record}
          seoKey="og_image"
          value={content.seo.og_image}
          hasCustom={Boolean(record?.og_image)}
          defaultNote="Using desktop hero image by default"
          onUploaded={(saved) => { setRecord(saved); setContent(merge(saved)); }}
          setNotice={setNotice}
        />
        <SeoImageField
          label="Twitter Image"
          record={record}
          seoKey="twitter_image"
          value={content.seo.twitter_image}
          hasCustom={Boolean(record?.twitter_image)}
          defaultNote="Using OG image by default"
          onUploaded={(saved) => { setRecord(saved); setContent(merge(saved)); }}
          setNotice={setNotice}
        />
      </Card>
      <Card title="Schema">
        <p style={{ margin: "0 0 10px", color: "#475569", fontSize: 13, lineHeight: 1.5 }}>
          WebPage and breadcrumb schemas are generated automatically for the Buyer page.
        </p>
        <p style={{ margin: "10px 0 16px", color: "#475569", fontSize: 13, lineHeight: 1.5 }}>
          FAQ schema is generated automatically from every FAQ with a question and answer.
        </p>
        <Field
          label="Custom JSON-LD"
          textarea
          value={content.schema.custom_json}
          {...fp("schema.custom_json")}
          onChange={(value) => setGroup("schema", "custom_json", value)}
        />
        <AdditionalSchemaGenerator
          schema={content.schema}
          errors={errors}
          onChange={(schema) => setContent((state) => ({ ...state, schema }))}
        />
        <p style={{ margin: "8px 0 0", color: "#64748b", fontSize: 12, lineHeight: 1.5 }}>
          The selected schema is generated from the Buyer page title, description, and canonical URL when this page is saved.
        </p>
      </Card>
    </div>
  );
}